import posthog from 'posthog-js';

const ANALYTICS_CONTEXT_KEY = 'menu-ai-analytics-context';
let runtimeContext = {};

const readStoredContext = () => {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(ANALYTICS_CONTEXT_KEY) || '{}');
  } catch (_error) {
    return {};
  }
};

const persistContext = context => {
  runtimeContext = { ...readStoredContext(), ...runtimeContext, ...context };
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(ANALYTICS_CONTEXT_KEY, JSON.stringify(runtimeContext));
  } catch (_error) {
    // Analytics must never block the product flow.
  }
};

const createScanSessionId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `scan-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const beginScanSession = ({ source, entryPoint } = {}) => {
  const scanSessionId = createScanSessionId();
  persistContext({ scanSessionId, source, entryPoint });
  return scanSessionId;
};

export const getCurrentScanSessionId = () => ({ ...readStoredContext(), ...runtimeContext }).scanSessionId;

export const updateProductAnalyticsContext = context => persistContext(context);

export const markSignupStarted = entryPoint => {
  persistContext({ signupPending: true, entryPoint: entryPoint || runtimeContext.entryPoint });
  captureProductEvent('account-signup-started', { entryPoint });
};

const getDeviceType = () => {
  if (typeof window === 'undefined') return 'unknown';

  const width = window.innerWidth || 0;
  if (width <= 767) return 'Mobile';
  if (width <= 1024) return 'Tablet';
  return 'Desktop';
};

const getUrlParam = (searchParams, key) => {
  const value = searchParams.get(key);
  return value || undefined;
};

const getReferringDomain = referrer => {
  if (!referrer) return undefined;

  try {
    return new URL(referrer).hostname;
  } catch (_error) {
    return undefined;
  }
};

export const getPageAnalyticsContext = () => {
  if (typeof window === 'undefined') return {};

  const { location, document, navigator } = window;
  const searchParams = new URLSearchParams(location.search);

  return {
    $host: location.hostname,
    $pathname: location.pathname,
    $current_url: location.href,
    $referrer: document.referrer || undefined,
    $referring_domain: getReferringDomain(document.referrer),
    $device_type: getDeviceType(),
    $browser_language: navigator.language,
    utm_source: getUrlParam(searchParams, 'utm_source'),
    utm_medium: getUrlParam(searchParams, 'utm_medium'),
    utm_campaign: getUrlParam(searchParams, 'utm_campaign'),
    utm_content: getUrlParam(searchParams, 'utm_content'),
    utm_term: getUrlParam(searchParams, 'utm_term'),
    entry_point: getUrlParam(searchParams, 'entry'),
    source_dish: getUrlParam(searchParams, 'dish'),
  };
};

const getProductContext = () => {
  const context = { ...readStoredContext(), ...runtimeContext };
  const hostname = typeof window === 'undefined' ? '' : window.location.hostname;
  const environment = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'unknown';
  const internalUser =
    environment !== 'production' || hostname === 'localhost' || hostname === '127.0.0.1';

  return {
    scanSessionId: context.scanSessionId,
    source: context.source,
    entryPoint: context.entryPoint,
    isAuthenticated: context.isAuthenticated,
    userPlan: context.userPlan,
    experimentVariant: context.experimentVariant,
    environment,
    internalUser,
  };
};

export const getPostHogDistinctId = () => {
  try {
    return posthog?.get_distinct_id?.();
  } catch (_error) {
    return undefined;
  }
};

export const captureProductEvent = (eventName, properties = {}) => {
  try {
    if (!posthog?.capture) return;

    posthog.capture(eventName, {
      ...getPageAnalyticsContext(),
      ...getProductContext(),
      ...properties,
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('PostHog capture failed:', eventName, error);
    }
  }
};

export const completePendingSignup = ({ isAuthenticated, userPlan } = {}) => {
  const context = { ...readStoredContext(), ...runtimeContext };
  persistContext({ isAuthenticated, userPlan });
  if (!isAuthenticated || !context.signupPending) return;

  captureProductEvent('account-signup-completed');
  persistContext({ signupPending: false });
};

export const buildAnalyticsHeaders = () => {
  const headers = {};
  const distinctId = getPostHogDistinctId();

  if (distinctId) {
    headers['X-PostHog-Distinct-Id'] = distinctId;
  }

  try {
    headers['X-Menu-Client-Context'] = JSON.stringify({
      ...getPageAnalyticsContext(),
      ...getProductContext(),
    });
  } catch (_error) {
    return headers;
  }

  return headers;
};
